import { Alert, Box, Snackbar } from '@mui/material'
import { useApp, Message } from '../hooks/useApp'
import { useDevices } from '../hooks/useDevices'
import { t } from 'i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowForward } from '@mui/icons-material'

const MessageLine = (props: { msg: Message }): JSX.Element => {
  const { text, type, url } = props.msg
  const navigate = useNavigate()

  const onClick = () => {
    if (url) {
      if (url.startsWith('/')) {
        navigate(url)
      } else {
        window.open(url)
      }
    }
  }

  return <Alert
    severity={type === 'announcement' ? 'info' : type}
    sx={{
      display: 'flex',
      borderRadius: 0,
      backgroundColor: type === 'announcement' ? 'black' : undefined,
      color: type === 'announcement' ? 'white' : undefined,
      padding: '0rem 1rem',
      border: '1px solid rgba(255,255,255,0.2)',
      cursor: url ? 'pointer' : 'inherit',
      ':hover': (url ? { backgroundColor: 'transparent' } : {})
    }}
    onClick={onClick}
  >
    {text}
    {url && <ArrowForward sx={{ ml: 2, verticalAlign: 'bottom' }}/>}
  </Alert>
}

/**
 * Display for UI messages.
 */
export const Messages = (): JSX.Element => {

  const app = useApp()
  const { control } = useDevices()
  const loc = useLocation()

  const atHome = (loc.pathname === '/home')

  const fixed: Message[] = []

  if (app.user?.announcements) {
    app.user.announcements.forEach(a => {
      fixed.push({
        type: 'announcement',
        text: a.text,
        url: a.url
      })
    })
  }

  // Single alert takes over everything except announcements.
  if (app.alert) {
    fixed.push({
      type: 'warning',
      text: t(app.alert),
    })
  } else {

    // Check all known fixed errors.
    if (app.installerLevel && atHome && control.hasProblems()) {
      fixed.push({
        type: 'error',
        text: t('Check Device Settings'),
        url: '/devices'
      })
    }

    if (control.isUpgrading()) {
      const progress = control.upgradeProgress()
      fixed.push({
        type: 'warning',
        text: t('Upgrading firmware. Please wait without changing settings. This will take few minutes.') +
          '\n' +
          progress.map(([dev, pct]) => `${dev} (${pct}%)`).join('\n'),
      })
    }

    if (app.user && app.user.settings && app.user.role !== 'ROOT' && app.user.role !== 'ADMIN') {
      const settings = app.user.settings as Record<string, string>
      const missing: string[] = []

      Object.entries({
        contractType: t('Electricity contract type'),
        transferPrice: t('Grid operator cost'),
        zipCode: t('Zip Code'),
      }).forEach(([variable, text]) => settings[variable] || missing.push(text))

      if ((settings.contractType === 'MARKET' || settings.contractType === 'MARKET_HOURLY') && !settings.marginPrice) {
        missing.push(t('Spot price marginal'))
      }
      if (settings.contractType === 'FIXED' && !settings.electricityPrice) {
        missing.push(t('Fixed price'))
      }
      if (settings.hasCheapTransfer && !settings.cheapStartTime) {
        missing.push(t('Cheap hours start'))
      }
      if (settings.hasCheapTransfer && !settings.cheapEndTime) {
        missing.push(t('Cheap hours end'))
      }
      if (settings.hasCheapTransfer && !settings.cheapTransferPrice) {
        missing.push(t('Reduced grid fee'))
      }

      if (missing.length) {
        fixed.push({
          type: 'error',
          text: t('Settings missing:') +
            ` '${missing.join("', '")}'. ` +
            t('In order to enable optimization please fill in the settings.'),
          url: '/settings'
        })
      }

      // Reserve terms require notifying the customer's balance responsible
      // party, which needs the metering point number plus either a Datahub
      // authorization (nagged about on the settings page itself) or a
      // self-selected retailer.
      if (settings.hasReserveMarkets) {
        const missingReserve: string[] = []
        const manualBrp = settings.brpMethod ? settings.brpMethod === 'manual' : true

        if (manualBrp && !settings.retailerCode) { missingReserve.push(t('Electricity retailer')) }
        if (!settings.meterNumber) { missingReserve.push(t('Meter Number')) }

        if (missingReserve.length) {
          fixed.push({
            type: 'error',
            text: t('Settings missing:') +
              ` '${missingReserve.join("', '")}'. ` +
              t('These are required for reserve market participation.'),
            url: '/settings'
          })
        }
      }
    }
  }

  return (
    <>
    {
      fixed.length > 0 && <Box sx={{ mt: 0.5, mb: 2 }}>
        {fixed.map((msg, idx) => <MessageLine key={idx} msg={msg}/>)}
      </Box>
    }

    {
      app.messages.length > 0 && <Snackbar data-cy="box-Toast Message" open>
        <Box>
          {app.messages.map((msg, idx) =>
            <Alert severity={msg.type === 'announcement' ? 'error' : msg.type} key={idx}>
              {msg.text}
            </Alert>
          )}
        </Box>
      </Snackbar>
    }
    </>
  )
}
